export interface LatLng {
  lat: number;
  lng: number;
}

const EARTH_RADIUS_KM = 6371;

function toRad(value: number): number {
  return (value * Math.PI) / 180;
}

export function haversineKm(a: LatLng, b: LatLng): number {
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const lat1 = toRad(a.lat);
  const lat2 = toRad(b.lat);
  const sinLat = Math.sin(dLat / 2);
  const sinLng = Math.sin(dLng / 2);
  const h = sinLat * sinLat + Math.cos(lat1) * Math.cos(lat2) * sinLng * sinLng;
  return 2 * EARTH_RADIUS_KM * Math.asin(Math.min(1, Math.sqrt(h)));
}

export function lerpPoint(a: LatLng, b: LatLng, t: number): LatLng {
  const clamped = Math.max(0, Math.min(1, t));
  return {
    lat: a.lat + (b.lat - a.lat) * clamped,
    lng: a.lng + (b.lng - a.lng) * clamped,
  };
}

/**
 * Build a slightly curved path between two points so the route looks natural
 * on a map instead of a perfectly straight line.
 */
export function curvedPath(a: LatLng, b: LatLng, steps = 24): LatLng[] {
  const points: LatLng[] = [];
  const dx = b.lng - a.lng;
  const dy = b.lat - a.lat;
  const length = Math.sqrt(dx * dx + dy * dy) || 1;
  // Perpendicular unit vector gives the curve its bow.
  const nx = -dy / length;
  const ny = dx / length;
  const bow = 0.12;
  for (let i = 0; i <= steps; i += 1) {
    const t = i / steps;
    const curve = Math.sin(t * Math.PI) * bow;
    points.push({
      lat: a.lat + dy * t + ny * curve,
      lng: a.lng + dx * t + nx * curve,
    });
  }
  return points;
}

/**
 * Deterministically derive a plausible "host start" position a few km away from
 * the pickup point, so the meetup simulation is stable between renders.
 */
export function hostStartPoint(pickup: LatLng, seed = 1): LatLng {
  const angle = (Math.sin(seed * 12.9898) * 43758.5453) % 1;
  const radius = 0.018 + Math.abs(Math.sin(seed * 78.233)) * 0.02; // ~2–4 km
  const theta = Math.abs(angle) * Math.PI * 2;
  return {
    lat: pickup.lat + Math.cos(theta) * radius,
    lng: pickup.lng + Math.sin(theta) * radius,
  };
}

export function formatDistance(km: number): string {
  if (km < 1) return `${Math.round(km * 1000)} m away`;
  return `${km.toFixed(1)} km away`;
}

export function etaMinutes(km: number, speedKmh = 28): number {
  return Math.max(1, Math.round((km / speedKmh) * 60));
}